import type { ActiveMission } from './types';

/**
 * Missions this browser launched, keyed by the aircraft flying them.
 *
 * Populated from the launch response itself (dock overlay and quick dispatch
 * both return the mission row they created), so it records what an operator
 * did on THIS screen — never a guess, and never a claim about a sortie some
 * other client started.
 */
export type LaunchedMissions = ReadonlyMap<string, string | null>;

/**
 * Which mission the aircraft's details panel should show.
 *
 * Drone id alone does not answer this. An airframe can hold more than one
 * mission in the active set — one planned for later today, or a previous sortie
 * still closing out — and the active-mission query orders by `scheduled_at`, so
 * taking the first match can surface the older of the two while a newer one is
 * actually flying. The backend already had to solve the same ambiguity to link a
 * flight to its mission (`missionRepo.findLatestInProgressForDrone`); this is
 * its screen-side twin, and it is exact rather than heuristic, because the
 * launch response named the mission outright.
 *
 * The pin is preferred, then the aircraft match. Falling through is not a
 * failure mode but the normal path for everything this browser did not launch:
 * another operator's sortie, the companion app, a mission started before this
 * screen was opened. It is also how a pin retires — once the aircraft lands the
 * mission leaves the active set, stops matching, and the lookup moves on
 * without anyone having to prune it.
 *
 * A pin is trusted only while the server still agrees the mission belongs to
 * this aircraft. It can stop agreeing: relaunching a saved mission from another
 * bay repoints `assigned_drone_id` to whichever airframe is flying it now (see
 * the dock launch handler). Without that check, an operator opening the ORIGINAL
 * aircraft would be shown a mission currently being flown by a different one —
 * the precise failure this whole binding exists to prevent, reintroduced from
 * the other side.
 */
export function resolveDroneMission(
  activeMissions: readonly ActiveMission[],
  droneId: string,
  launchedMissions: LaunchedMissions,
): ActiveMission | null {
  if (launchedMissions.has(droneId)) {
    const pinnedId = launchedMissions.get(droneId);
    // A null pin is knowledge, not absence: this aircraft launched on a sortie
    // the backend kept no mission row for. Falling back to the drone match here
    // would put some OTHER mission's waypoints on screen against an aircraft
    // known not to be flying it — the exact lie this module exists to stop.
    // Reporting nothing is the honest answer until it launches again.
    if (pinnedId == null) return null;
    const pinned = activeMissions.find((m) => m.id === pinnedId);
    if (pinned && pinned.assigned_drone_id === droneId) return pinned;
  }
  return activeMissions.find((m) => m.assigned_drone_id === droneId) ?? null;
}

/**
 * Records the sortie a launch just created, replacing any earlier pin for that
 * aircraft.
 *
 * A null `missionId` means the launch was accepted but the backend could not
 * write a mission row for it — it deliberately logs and swallows that rather
 * than failing a launch the GO/NO-GO gate already cleared. That is recorded as a
 * null pin rather than dropped, because "flying, no record" and "nothing known"
 * are different states and only the first one can be told from the second by
 * having been stored. See resolveDroneMission for what it suppresses.
 *
 * Known limitation: a null pin keeps suppressing the fallback until this screen
 * launches that aircraft again, so a sortie another operator starts for it will
 * not appear here. That is the safe direction to fail — silence rather than the
 * wrong route — and it needs a backend-side binding to fix properly.
 */
export function pinLaunchedMission(
  launchedMissions: LaunchedMissions,
  droneId: string,
  missionId: string | null,
): LaunchedMissions {
  return new Map(launchedMissions).set(droneId, missionId);
}
